import { useCallback, useLayoutEffect, useMemo, useReducer } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { useEditorRemixCommands } from '@atlassian/malleable-ui-components/editor-remix/commands';
import type { EditorRemixCommandHandlers } from '@atlassian/malleable-ui-components/editor-remix/commands';
import { useEditorRemixLifecycle } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import {
	DEFAULT_REMIX_ERROR_MESSAGE,
	REMIX_REMOTE_ABORT_REASON,
} from '@atlassian/malleable-ui-components/types';

import { setRemixThinkingLifecycle } from '../editor-commands/set-remix-thinking-lifecycle';
import type { MalleableUiPlugin } from '../malleableUiPluginType';

import { useEditorRemixLifecycleMirror } from './editorRemixLifecycleMirror';
import type { RemixThinkingLifecycleProjection } from './utils/editor-remix-lifecycle-projection';
import {
	getEditorRemixLifecyclePresentation,
	getEditorRemixUxHandles,
	subscribeToEditorRemixLifecyclePresentation,
} from './utils/editor-remix-callback-registry';

/**
 * Sibling of `RemixContent`, not a descendant, so it survives the terminal cleanup that unmounts
 * that subtree. The mount in `malleableUiPlugin` already gates this on the state-machine cohort.
 *
 * It owns both halves of the machine's authority over this surface: the lifecycle mirror writes
 * ProseMirror lifecycle, and the command handlers own every terminal product effect. The UX closures
 * they drive live in `RemixContentComponentInner`, which terminal cleanup unmounts — so they are read
 * from the registry at invocation time rather than captured here.
 */
export const EditorRemixEffectHost = ({
	api,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
}): null => {
	const { remixId } = useEditorRemixLifecycle();
	const [, refreshPresentation] = useReducer((count: number) => count + 1, 0);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useLayoutEffect(
		() => subscribeToEditorRemixLifecyclePresentation(api, refreshPresentation),
		[api, remixId],
	);
	const presentation = getEditorRemixLifecyclePresentation(api);

	const dispatchProjection = useCallback(
		(projection: RemixThinkingLifecycleProjection) => {
			api?.core.actions.execute(setRemixThinkingLifecycle(projection));
		},
		[api],
	);

	useEditorRemixLifecycleMirror({
		animationFinished: presentation.animationFinished,
		presentationRemixId: presentation.remixId ?? null,
		dispatchProjection,
	});

	const commandHandlers = useMemo<EditorRemixCommandHandlers>(
		() => ({
			onReady: () => {
				// Completion itself is already the mirror's, off the machine's `ready` phase. This only
				// starts the local completion animation, and must write no ProseMirror lifecycle.
				getEditorRemixUxHandles(api)?.markReady();
			},
			onErrored: () => {
				const handles = getEditorRemixUxHandles(api);
				if (!handles) {
					return;
				}
				handles.settleExperience({
					kind: 'failed',
					// The content component knows the Rovo store's error; this is the fallback for a
					// command that arrives with no store error to describe, and it is the message legacy
					// falls back to in the same case.
					error: new Error(DEFAULT_REMIX_ERROR_MESSAGE),
				});
				// This order is the legacy error path's, and `stopThinking` before `closeModal` is the
				// load-bearing part of it. `stopThinking({ reopenModal: true })` writes
				// `isRemixModalOpen: true` and `closeModal` writes `isRemixModalOpen: false`, both on
				// `malleableUiPluginKey`, so the later call wins: inverting them leaves the modal open on
				// error. `reopenModal` is still required, because it is what preserves the saved selection
				// while the thinking UI is dismissed.
				//
				// Nothing here needs the lifecycle write-authority lease to outlive `stopThinking`: none of
				// the other handles writes a ProseMirror lifecycle facet, and `stopThinking` holds the lease
				// for its own duration through `captureEditorRemixTerminalCleanupSink`.
				handles.stopThinking({ reopenModal: true });
				handles.closeTerminalPreview();
				handles.closeModal();
				handles.openSidebar();
			},
			onAborted: (command) => {
				const handles = getEditorRemixUxHandles(api);
				if (!handles) {
					return;
				}
				// `RemixAborted` is origin-blind, so it settles with the reason legacy reports for an
				// abort this surface did not initiate, keeping the two cohorts comparable. Legacy
				// conflates the same cases under that wording, so matching it reproduces legacy
				// exactly, and the content component still overrides it with a recorded local intent.
				if (
					handles.settleExperience({
						kind: 'aborted',
						reason: REMIX_REMOTE_ABORT_REASON,
						remixId: command.remixId,
					}) === 'ignored'
				) {
					// The flow already committed a different terminal outcome, so this abort is not one:
					// a Rovo Chat handoff completes with no remix action, which the machine derives as
					// `aborted`. Its own teardown ran with it, so stopping the thinking UI here would tear
					// down UI that the handoff is using.
					return;
				}
				handles.stopThinking({ reopenModal: false });
			},
			onCancellationSettled: () => {
				// Only reached by a `cancelled` teardown that no terminal command settled. The teardown's
				// own UX has already run; this closes the experience the fence moved off it.
				getEditorRemixUxHandles(api)?.settleCancellation();
			},
			onHandlerError: ({ commandType, error }) => {
				try {
					createAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'editorRemixCommandHandler',
						actionSubjectId: commandType,
						source: 'editorRemix',
						attributes: {
							operation: 'commandEffect',
							commandType,
							// Bounded and controlled, the way `logLifecycleOperationError` reports an operation
							// failure. Deliberately not `error.message`: that can carry service-supplied text.
							// This is the only place a half-completed terminal cleanup surfaces, so it has to
							// say more than "a handler for this command type threw".
							errorName: error instanceof Error ? error.name : typeof error,
						},
					}).fire();
				} catch {
					// Diagnostics must never affect command handling.
				}
			},
		}),
		[api, createAnalyticsEvent],
	);

	useEditorRemixCommands(commandHandlers);

	return null;
};
